import { TAGS } from "../constants";
import { IntakeQuestionOption, IntakeQuestionOptionUpdate } from "../types";
import { api } from "./api";

export const intakeQuestionOptionsApi = api.injectEndpoints({
  endpoints: (builder) => ({
    getIntakeQuestionOptions: builder.query<
      IntakeQuestionOption[],
      { intakeId: number; questionId: number }
    >({
      query: ({ questionId, intakeId }) => ({
        method: "GET",
        url: `/api/intakes/${intakeId}/questions/${questionId}/options`,
      }),
      providesTags: (_returns, _error, args) => [
        { type: TAGS.INTAKE_QUESTION_OPTIONS, id: args.questionId },
      ],
    }),

    createIntakeQuestionOption: builder.mutation<
      IntakeQuestionOption[],
      {
        intakeId: number;
        questionId: number;
        body: {
          option_id: number;
          sort_order: number;
          next_question?: string;
        }[];
      }
    >({
      query: ({ intakeId, questionId, body }) => ({
        method: "POST",
        url: `/api/intakes/${intakeId}/questions/${questionId}/options`,
        body,
      }),
      invalidatesTags: (_return, _error, args) => [
        { type: TAGS.INTAKE_QUESTIONS, id: args.intakeId },
        { type: TAGS.INTAKE_QUESTION, id: args.intakeId },
      ],
    }),
    updateIntakeQuestionOption: builder.mutation<
      IntakeQuestionOption,
      {
        intakeId: number;
        questionId: number;
        optionId: number;
        body: IntakeQuestionOptionUpdate;
      }
    >({
      query: ({ intakeId, questionId, optionId, body }) => ({
        method: "PUT",
        url: `/api/intakes/${intakeId}/questions/${questionId}/options/${optionId}`,
        body,
      }),
      onQueryStarted: async (args, { dispatch, queryFulfilled }) => {
        const patch = dispatch(
          intakeQuestionOptionsApi.util.updateQueryData(
            "getIntakeQuestionOptions",
            { questionId: args.questionId, intakeId: args.intakeId },
            (draft) => {
              const option = draft.find((o) => o.id === args.optionId);
              if (!option) return;

              if ("next_question" in args.body) {
                option.next_question = {
                  slug: args.body.next_question ?? "",
                  title: option.next_question?.slug ?? "",
                };
              }

              if (args.body.sort_order != null) {
                option.position = args.body.sort_order;
              }
            },
          ),
        );

        queryFulfilled.catch(patch.undo);
      },
    }),
    bulkUpdateQuestionOptions: builder.mutation<
      IntakeQuestionOption[],
      {
        intakeId: number;
        questionId: number;
        body: IntakeQuestionOptionUpdate[];
      }
    >({
      query: ({ body, intakeId, questionId }) => ({
        method: "PUT",
        url: `/api/intakes/${intakeId}/questions/${questionId}/options/bulk-update`,
        body,
      }),
      onQueryStarted: async (args, { dispatch, queryFulfilled }) => {
        const patch = dispatch(
          intakeQuestionOptionsApi.util.updateQueryData(
            "getIntakeQuestionOptions",
            { intakeId: args.intakeId, questionId: args.questionId },
            (draft) => {
              args.body.forEach(({ option_id, sort_order }) => {
                const option = draft.find((o) => o.id === option_id);
                if (option != null && sort_order != null) {
                  option.position = sort_order;
                }
              });
            },
          ),
        );

        queryFulfilled.catch(patch.undo);
      },
    }),
  }),
});

export const {
  useGetIntakeQuestionOptionsQuery,
  useCreateIntakeQuestionOptionMutation,
  useUpdateIntakeQuestionOptionMutation,
  useBulkUpdateQuestionOptionsMutation,
} = intakeQuestionOptionsApi;
